<template>
  <div v-bind='$attrs'>
    <label
      v-if='label'
      :for='name'
      class='relative block text-sm font-normal'
      :class='[
        (disabled && editMode) ? "text-gray-400" : "text-gray-700",
        editMode ? "w-full" : "",
      ]'
      data-cy='label'
      @click='toggle'
    >
      <CheckCircleIcon
        v-if='!editMode && modelValue'
        class='absolute top-0.5 -left-5 inline-block h-4 w-4 text-green-600'
      />
      <XCircleIcon
        v-if='!editMode && !modelValue'
        class='absolute top-0.5 -left-5 inline-block h-4 w-4 text-gray-500'
      />
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click.stop='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <div v-if='editMode'>
      <div class='relative mt-1 flex flex-row-reverse items-center gap-3'>
        <div
          :disabled='disabled'
          :class='[modelValue ? "bg-green-600" : "bg-gray-200", "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"]'
          :tabindex='disabled ? -1 : 0'
          data-cy='switch-button'
          @click='toggle'
          @keydown.enter='toggle'
        >
          <span
            :class='[modelValue ? "translate-x-5" : "translate-x-0", "pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"]'
          >
            <span
              :class='[modelValue ? "opacity-0 ease-out duration-100" : "opacity-100 ease-in duration-200", "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"]'
              aria-hidden='true'
            >
              <svg
                class='h-3 w-3 text-gray-400'
                fill='none'
                viewBox='0 0 12 12'
              >
                <path
                  d='M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2'
                  stroke='currentColor'
                  stroke-width='2'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                />
              </svg>
            </span>
            <span
              :class='[modelValue ? "opacity-100 ease-in duration-200" : "opacity-0 ease-out duration-100", "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"]'
              aria-hidden='true'
            >
              <svg
                class='h-3 w-3 text-green-600'
                fill='currentColor'
                viewBox='0 0 12 12'
              >
                <path d='M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z' />
              </svg>
            </span>
          </span>
        </div>
        <div
          class='text-sm'
          :class='{"text-red-600": error}'
          data-cy='text-value'
        >
          <div v-if='proxyValue && proxyValue !== null'>
            {{ proxyValue instanceof Date ? proxyValue.toLocaleDateString('en-GB') : proxyValue }}
          </div>
          <div v-else>No</div>
        </div>
      </div>
      <InputError :error='error'/>
    </div>
    <div v-else>
      <div
        class='text-sm'
        data-cy='text-value'
      >
        <div v-if='proxyValue && proxyValue !== null'>
          {{ proxyValue instanceof Date ? proxyValue.toLocaleDateString('en-GB') : proxyValue }}
        </div>
        <div
          v-else
          class='item-center flex flex-row space-x-2'
        >
          <span>No</span>
        </div>
      </div>
    </div>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { defineComponent, onMounted, ref, watch } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { useToString } from '@vueuse/core/index'
  import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/outline'

  export default defineComponent({
    name: 'TimestampSwitchField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      CheckCircleIcon,
      XCircleIcon,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
    },
    emits: [
      ...InputFieldEmits,
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, getLabel } = useInputFieldBase()

      const proxyValue = ref()

      watch(() => props.modelValue, (value) => {
        proxyValue.value = value
      })

      onMounted(() => {
        if(props.modelValue) {
          if(useToString(props.modelValue) && (props.modelValue !== '0')) {
            proxyValue.value = new Date(props.modelValue)
          } else {

            if (props.modelValue === '0') proxyValue.value = false
            else proxyValue.value = props.modelValue

          }
        } else {
          proxyValue.value = false
        }
      })

      function toggle() {
        if(!props.disabled && props.editMode) {
          updateValue(proxyValue.value ? false : new Date())
        }
      }

      return {
        updateValue, helpVisible, setHelpModalVisibility, proxyValue, toggle, getLabel,
      }
    },
  })
</script>
